import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/ui/cn';

// The wireframe's buttons sit on a solid ledge (`shadow-press*`) that collapses
// when pressed, so a click feels physical. Teal is the action colour; purple is
// kept for identity and the occasional brand-weight action.
const PRESS = 'enabled:active:translate-y-[2px] enabled:active:shadow-none';

const VARIANTS = {
  primary: cn(
    'border border-accent-700 bg-accent-500 text-white shadow-press-accent',
    'hover:bg-accent-600 disabled:hover:bg-accent-500',
    PRESS,
  ),
  brand: cn(
    'border border-brand-900 bg-brand-500 text-on-brand shadow-press-brand',
    'hover:bg-brand-400 disabled:hover:bg-brand-500',
    PRESS,
  ),
  secondary: cn(
    'border border-brand-200/70 bg-surface text-content shadow-press',
    'hover:border-brand-500 hover:text-brand-500',
    'disabled:hover:border-brand-200/70 disabled:hover:text-content',
    PRESS,
  ),
  ink: 'bg-ink text-white hover:bg-ink-soft disabled:hover:bg-ink',
  accent: 'bg-accent-500 text-white hover:bg-accent-600 disabled:hover:bg-accent-500',
  // The pill outline used for "Visit store" and other tertiary actions.
  outline: 'bg-transparent border-[1.5px] border-accent-600 text-accent-700 hover:bg-accent-50',
  // Red text on white, as the wireframe draws it: destructive, but not shouting.
  danger: cn(
    'border border-danger/30 bg-surface text-danger shadow-[0_2px_0_rgb(var(--danger)/0.3)]',
    'hover:border-danger hover:bg-danger/10',
    PRESS,
  ),
  ghost: 'bg-transparent text-content-muted hover:bg-surface hover:text-brand-500',
  link: 'bg-transparent text-accent-500 font-semibold hover:underline p-0 h-auto',
};

const SIZES = {
  sm: 'h-8 rounded-[9px] px-3 text-[13px] gap-1.5',
  md: 'h-10 rounded-[11px] px-4 text-sm gap-2',
  lg: 'h-12 rounded-[13px] px-6 text-[15px] gap-2',
};

export const Button = forwardRef(function Button(
  {
    as: Component = 'button',
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    leftIcon,
    rightIcon,
    className,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading;
  // `as` lets a link wear the button's clothes - <Button as={Link}> - without a
  // parallel ButtonLink that would drift. `type` and `disabled` are button-only
  // attributes, so they are applied only when this really is a <button>; on an
  // anchor they would be invalid DOM and, worse, `disabled` would not disable.
  const isNativeButton = Component === 'button';

  return (
    <Component
      ref={ref}
      {...(isNativeButton
        ? { type, disabled: isDisabled }
        : isDisabled
          ? { 'aria-disabled': true, tabIndex: -1, onClick: (event) => event.preventDefault() }
          : {})}
      // Communicate the busy state to assistive tech, not just visually.
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-semibold',
        'transition-[transform,box-shadow,background-color,border-color,color] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-45',
        VARIANTS[variant],
        variant !== 'link' && SIZES[size],
        fullWidth && 'w-full',
        isDisabled && !isNativeButton && 'pointer-events-none opacity-60',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </Component>
  );
});
